import { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAuth } from '../../context/AuthContext';
import { useAsync } from '../../lib/useAsync';
import { storiesApi } from '../../api/resources';
import { StatusBadge, PageLoader, ErrorState, EmptyState } from '../../components/ui';
import { useToast } from '../../context/ToastContext';
import { formatDate } from '../../lib/format';
import SubmissionPreview from '../../components/SubmissionPreview';
import { normalizeSubmission } from '../../components/submissionTypes';

export default function MyStories() {
  const { user } = useAuth();
  const toast = useToast();
  const { data, loading, error, reload } = useAsync(() => storiesApi.list({ per_page: 50 }), []);
  // Read-only "how it will look published" preview, any status.
  const [preview, setPreview] = useState(null);

  // The endpoint returns published stories plus the member's own drafts, so
  // filter to the signed-in author (never another member's rows).
  const mine = (data?.items || []).filter((s) => s.author?.id === user?.id);

  async function handleSubmit(id) {
    try {
      await storiesApi.submit(id, 'Submitted for review.');
      toast.success('Story submitted for review.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not submit.');
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this story? This cannot be undone.')) return;
    try {
      await storiesApi.remove(id);
      toast.success('Story deleted.');
      reload();
    } catch (err) {
      toast.error(err.message || 'Could not delete.');
    }
  }

  if (loading) return <PageLoader />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  return (
    <div className="space-y-6">
      <Seo title="My Stories" />
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl font-bold">My Stories</h1>
        <Link to="/member/stories/new" className="btn-primary">
          + New Story
        </Link>
      </div>

      <p className="text-sm text-ink-muted">
        Every story you have written, from drafts to published. Use Preview to see exactly how a
        story looks before or after it goes live.
      </p>

      {mine.length === 0 ? (
        <EmptyState
          title="No stories yet"
          message="Share your community impact by writing your first story."
          action={
            <Link to="/member/stories/new" className="btn-primary">
              Write a story
            </Link>
          }
        />
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-cream-300 bg-surface-alt text-xs uppercase tracking-wider text-ink-soft">
              <tr>
                <th className="px-5 py-3">Title</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Updated</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cream-300">
              {mine.map((s) => (
                <tr key={s.id} className="hover:bg-surface-alt">
                  <td className="px-5 py-3 font-medium text-ink">{s.title}</td>
                  <td className="px-5 py-3">
                    <StatusBadge status={s.status} />
                  </td>
                  <td className="px-5 py-3 text-ink-muted">{formatDate(s.updated_at || s.created_at)}</td>
                  <td className="px-5 py-3">
                    <div className="flex flex-wrap justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setPreview(normalizeSubmission(s, 'stories'))}
                        className="btn-outline px-3 py-1.5 text-xs"
                        aria-label={`Preview ${s.title}`}
                      >
                        Preview
                      </button>
                      {/* StoryPolicy denies update/delete once published, so the
                          author gets the public link instead of dead actions. */}
                      {s.status === 'published' ? (
                        <a
                          href={`/stories/${s.slug || s.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn-ghost px-3 py-1.5 text-xs"
                        >
                          View
                        </a>
                      ) : (
                        <Link to={`/member/stories/${s.id}/edit`} className="btn-ghost px-3 py-1.5 text-xs">
                          Edit
                        </Link>
                      )}
                      {(s.status === 'draft' || s.status === 'rejected') && (
                        <button onClick={() => handleSubmit(s.id)} className="btn-gold px-3 py-1.5 text-xs">
                          Submit
                        </button>
                      )}
                      {s.status !== 'published' && (
                        <button
                          onClick={() => handleDelete(s.id)}
                          className="btn-ghost px-3 py-1.5 text-xs text-red-600"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SubmissionPreview item={preview} onClose={() => setPreview(null)} readOnly />
    </div>
  );
}
